import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  listArticleTypes,
  listEnabledArticleTypes,
  getArticleTypeById,
  createArticleType, 
  updateArticleType, 
  deleteArticleType
} from '../../api/articleType';
import { message } from 'antd';

// 分页查询资讯文章类型列表
export const fetchArticleTypes = createAsyncThunk(
  'articleType/fetchList',
  async ({ page = 1, size = 10, queryDTO = {} }, { rejectWithValue }) => {
    try {
      const response = await listArticleTypes(page, size, queryDTO);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '获取文章类型列表失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// 获取所有启用的文章类型列表
export const fetchEnabledArticleTypes = createAsyncThunk(
  'articleType/fetchEnabled',
  async (_, { rejectWithValue }) => {
    try {
      const response = await listEnabledArticleTypes();
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '获取启用的文章类型列表失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// 根据ID获取文章类型详情
export const fetchArticleTypeById = createAsyncThunk(
  'articleType/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await getArticleTypeById(id);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '获取文章类型详情失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// 创建文章类型
export const createArticleTypeAsync = createAsyncThunk(
  'articleType/create',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await createArticleType(data);
      message.success('创建成功');
      // 创建成功后重新获取列表
      dispatch(fetchArticleTypes({ page: 1, size: 10 }));
      dispatch(fetchEnabledArticleTypes());
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '创建失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// 更新文章类型
export const updateArticleTypeAsync = createAsyncThunk(
  'articleType/update',
  async ({ id, data }, { rejectWithValue, dispatch }) => {
    try {
      const response = await updateArticleType(id, data);
      message.success('更新成功');
      // 更新成功后重新获取列表
      dispatch(fetchArticleTypes({ page: 1, size: 10 }));
      dispatch(fetchEnabledArticleTypes());
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '更新失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// 删除文章类型
export const deleteArticleTypeAsync = createAsyncThunk(
  'articleType/delete',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await deleteArticleType(id);
      message.success('删除成功');
      // 删除成功后重新获取列表
      dispatch(fetchArticleTypes({ page: 1, size: 10 }));
      dispatch(fetchEnabledArticleTypes());
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '删除失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

const articleTypeSlice = createSlice({
  name: 'articleType',
  initialState: {
    articleTypes: [],
    enabledArticleTypes: [],
    total: 0,
    currentPage: 1,
    pageSize: 10,
    loading: false,
    currentArticleType: null,
    error: null
  },
  reducers: {
    setPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
    },
    setCurrentArticleType: (state, action) => {
      state.currentArticleType = action.payload;
    },
    clearCurrentArticleType: (state) => {
      state.currentArticleType = null;
    }
  },
  extraReducers: (builder) => {
    // 查询列表
    builder.addCase(fetchArticleTypes.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchArticleTypes.fulfilled, (state, action) => {
      state.loading = false;
      state.articleTypes = action.payload.records || [];
      state.total = action.payload.total || 0;
      state.currentPage = action.payload.current || action.payload.page || 1;
      state.pageSize = action.payload.size || 10;
    });
    builder.addCase(fetchArticleTypes.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 获取启用的类型列表
    builder.addCase(fetchEnabledArticleTypes.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchEnabledArticleTypes.fulfilled, (state, action) => {
      state.loading = false;
      // 如果返回的是数组，直接使用；如果是对象，取records或data
      const payload = action.payload;
      if (Array.isArray(payload)) {
        state.enabledArticleTypes = payload.filter(item => item.status === 1);
      } else if (payload?.records) {
        state.enabledArticleTypes = payload.records.filter(item => item.status === 1);
      } else if (payload?.data) {
        state.enabledArticleTypes = Array.isArray(payload.data) 
          ? payload.data.filter(item => item.status === 1)
          : [];
      } else {
        state.enabledArticleTypes = [];
      }
    });
    builder.addCase(fetchEnabledArticleTypes.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 获取详情
    builder.addCase(fetchArticleTypeById.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchArticleTypeById.fulfilled, (state, action) => {
      state.loading = false;
      state.currentArticleType = action.payload;
    });
    builder.addCase(fetchArticleTypeById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 创建
    builder.addCase(createArticleTypeAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createArticleTypeAsync.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(createArticleTypeAsync.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 更新
    builder.addCase(updateArticleTypeAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateArticleTypeAsync.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(updateArticleTypeAsync.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 删除
    builder.addCase(deleteArticleTypeAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteArticleTypeAsync.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteArticleTypeAsync.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
  }
});

export const { setPage, setPageSize, setCurrentArticleType, clearCurrentArticleType } = articleTypeSlice.actions;
export default articleTypeSlice.reducer;

